import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight, Home } from 'lucide-react';

/**
 * Breadcrumb Component
 * Provides navigation context showing the current page hierarchy.
 * 
 * Usage:
 * <Breadcrumb items={[
 *   { label: 'Dashboard', path: '/admin/dashboard' },
 *   { label: 'Applicants', path: '/applicants' },
 *   { label: 'John Smith' }  // Current page (no path)
 * ]} />
 */
const Breadcrumb = ({ items = [], className = '' }) => {
  const location = useLocation();

  if (items.length === 0) return null;

  return (
    <nav 
      className={`flex items-center text-sm text-gray-500 mb-4 ${className}`}
      aria-label="Breadcrumb"
      data-testid="breadcrumb-nav"
    >
      <ol className="flex items-center flex-wrap gap-1">
        {/* Home link */}
        <li className="flex items-center">
          <Link 
            to="/admin/dashboard" 
            className="hover:text-blue-600 transition-colors flex items-center gap-1"
            data-testid="breadcrumb-home"
          >
            <Home className="w-4 h-4" />
            <span className="sr-only sm:not-sr-only">Dashboard</span>
          </Link>
        </li>

        {items.map((item, index) => {
          const isLast = index === items.length - 1;
          const isActive = item.path === location.pathname;

          return (
            <li key={index} className="flex items-center">
              <ChevronRight className="w-4 h-4 mx-1 text-gray-400" />
              {item.path && !isLast ? (
                <Link
                  to={item.path}
                  className={`hover:text-blue-600 transition-colors ${
                    isActive ? 'text-blue-600 font-medium' : ''
                  }`}
                  data-testid={`breadcrumb-${index}`}
                >
                  {item.label}
                </Link>
              ) : (
                <span 
                  className={`${isLast ? 'text-gray-900 font-medium' : 'text-gray-500'}`}
                  data-testid={`breadcrumb-${index}`}
                  aria-current={isLast ? 'page' : undefined}
                >
                  {item.label}
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

export default Breadcrumb;
